import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'
import type {
  InstructorSplit,
  InstructorCourseResponse,
  AssignInstructorInput,
  UpdateSplitInput,
  ValidateSplitsInput,
  ValidationResult,
} from '@/types/instructorSplit'

export const instructorSplitService = {
  /**
   * Get all instructor splits for a specific course
   * GET /api/v1/courses/:courseId/instructors
   * @param courseId - The course ID
   * @returns Promise with array of instructor splits
   */
  listByCourse: async (courseId: string): Promise<ApiResponse<InstructorSplit[]>> => {
    return apiClient.get<InstructorSplit[]>(`/courses/${courseId}/instructors`)
  },

  /**
   * Assign an instructor to a course with split configuration
   * POST /api/v1/courses/:courseId/instructors
   * @param courseId - The course ID
   * @param input - The instructor assignment data
   * @returns Promise with created instructor split
   */
  assignInstructor: async (
    courseId: string,
    input: AssignInstructorInput
  ): Promise<ApiResponse<InstructorSplit>> => {
    // Interceptor in api.ts handles camelCase to snake_case transformation
    return apiClient.post<InstructorSplit>(`/courses/${courseId}/instructors`, {
      userId: input.userId,
      splitPercentage: input.splitPercentage ?? 0,
      fixedAmount: input.fixedAmount ?? 0,
      moduleAssignments: input.moduleAssignments ?? [],
    })
  },

  /**
   * Update an instructor's split configuration for a course
   * PUT /api/v1/courses/:courseId/instructors/:userId
   * @param courseId - The course ID
   * @param userId - The instructor user ID
   * @param input - The update data
   * @returns Promise with updated instructor split
   */
  updateSplit: async (
    courseId: string,
    userId: string,
    input: UpdateSplitInput
  ): Promise<ApiResponse<InstructorSplit>> => {
    // Interceptor in api.ts handles camelCase to snake_case transformation
    return apiClient.put<InstructorSplit>(`/courses/${courseId}/instructors/${userId}`, {
      splitPercentage: input.splitPercentage,
      fixedAmount: input.fixedAmount,
      moduleAssignments: input.moduleAssignments,
    })
  },

  /**
   * Remove an instructor from a course
   * DELETE /api/v1/courses/:courseId/instructors/:userId
   * @param courseId - The course ID
   * @param userId - The instructor user ID
   * @returns Promise with void response
   */
  removeInstructor: async (courseId: string, userId: string): Promise<ApiResponse<void>> => {
    return apiClient.delete<void>(`/courses/${courseId}/instructors/${userId}`)
  },

  /**
   * Validate instructor split percentages for a course
   * POST /api/v1/courses/:courseId/instructors/validate
   * @param courseId - The course ID
   * @param input - The splits to validate
   * @returns Promise with validation result
   */
  validateSplits: async (
    courseId: string,
    input: ValidateSplitsInput
  ): Promise<ApiResponse<ValidationResult>> => {
    // Interceptor in api.ts handles camelCase to snake_case transformation
    return apiClient.post<ValidationResult>(`/courses/${courseId}/instructors/validate`, input)
  },

  /**
   * Get all courses assigned to a specific instructor
   * GET /api/v1/instructors/:userId/courses
   * @param userId - The instructor user ID
   * @returns Promise with array of instructor courses with details
   */
  getCoursesByInstructor: async (userId: string): Promise<ApiResponse<InstructorCourseResponse[]>> => {
    return apiClient.get<InstructorCourseResponse[]>(`/instructors/${userId}/courses`)
  },

  /**
   * Get assigned modules for an instructor in a specific course
   * GET /api/v1/instructors/:userId/courses/:courseId/modules
   * @param userId - The instructor user ID
   * @param courseId - The course ID
   * @returns Promise with array of assigned modules
   */
  getAssignedModules: async (userId: string, courseId: string): Promise<ApiResponse<any[]>> => {
    return apiClient.get<any[]>(`/instructors/${userId}/courses/${courseId}/modules`)
  },
}
